import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import {
  Loader2, Download, ShieldCheck, ClipboardCheck, AlertTriangle,
  CheckCircle2, CalendarClock, ArrowRight, KeyRound, Mail,
} from "lucide-react";
import { Helmet } from "react-helmet-async";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const RATING_TILE = {
  GREEN: { bg: "bg-[#064E2E]", label: "Good Condition", icon: <CheckCircle2 size={14}/> },
  AMBER: { bg: "bg-[#B88A2A]", label: "Monitor", icon: <AlertTriangle size={14}/> },
  RED:   { bg: "bg-[#B91C1C]", label: "Action Required", icon: <AlertTriangle size={14}/> },
};

export default function MyHealthChecks() {
  const [params] = useSearchParams();
  const initialEmail = params.get("email") || "";
  const initialToken = params.get("token") || "";

  const [email, setEmail] = useState(initialEmail);
  const [token, setToken] = useState(initialToken);
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const autoTriedRef = useRef(false);

  async function lookup(e) {
    if (e) e.preventDefault();
    setError("");
    if (!email.trim() || !token.trim()) {
      setError("Please enter both your email and the report code from your latest email.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.get(`${BACKEND}/api/member/health-checks`, {
        params: { email: email.trim().toLowerCase(), member_token: token.trim() },
      });
      setItems(data.items || []);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "We couldn't verify your identity. Check your email and the latest report code.");
      setItems(null);
    } finally {
      setLoading(false);
    }
  }

  // If the page is opened with ?email=...&token=... in the URL, run the lookup once.
  useEffect(() => {
    if (autoTriedRef.current) return;
    if (!initialEmail || !initialToken) return;
    autoTriedRef.current = true;
    let active = true;
    (async () => {
      try {
        const { data } = await axios.get(`${BACKEND}/api/member/health-checks`, {
          params: { email: initialEmail.trim().toLowerCase(), member_token: initialToken.trim() },
        });
        if (active) setItems(data.items || []);
      } catch (err) {
        if (!active) return;
        const detail = err.response?.data?.detail;
        setError(typeof detail === "string" ? detail : "We couldn't verify your identity. Check your email and the latest report code.");
        setItems(null);
      }
    })();
    return () => { active = false; };
  }, [initialEmail, initialToken]);

  return (
    <div className="bg-[#F7F4ED] min-h-[60vh]" data-testid="my-health-checks-page">
      <Helmet>
        <title>My Health Checks · Park Home Support</title>
        <meta name="robots" content="noindex,nofollow"/>
      </Helmet>

      <div className="container-phs py-10 lg:py-14">
        <div className="max-w-3xl">
          <p className="eyebrow">Member Area</p>
          <h1 className="font-display text-3xl sm:text-4xl text-[#071A33] mt-1">Your Annual Health Checks</h1>
          <p className="mt-2 text-[#475569] text-[15px] leading-relaxed">
            View every Annual External Park Home Health Check we&apos;ve completed for your home, including the overall rating,
            recommendations and a downloadable PDF report.
          </p>
        </div>

        {items === null ? (
          <form
            onSubmit={lookup}
            className="mt-8 card-default max-w-xl space-y-4"
            data-testid="my-hc-lookup-form"
          >
            <div className="flex items-start gap-3">
              <ShieldCheck className="text-[#064E2E] shrink-0 mt-1" size={20}/>
              <p className="text-sm text-[#334155] leading-relaxed">
                For your security, we ask for two things to confirm it&apos;s really you:
              </p>
            </div>

            <div>
              <label className="field-label flex items-center gap-1.5"><Mail size={12}/> The email on your membership</label>
              <input
                type="email"
                className="field-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                data-testid="my-hc-email-input"
                autoComplete="email"
              />
            </div>

            <div>
              <label className="field-label flex items-center gap-1.5"><KeyRound size={12}/> Report code from your latest report email</label>
              <input
                className="field-input"
                value={token}
                onChange={(e) => setToken(e.target.value)}
                placeholder="e.g. 7c2a9bf1-... (from your report link)"
                data-testid="my-hc-token-input"
              />
              <p className="text-[11px] text-[#475569] mt-1.5 leading-relaxed">
                The report code is the long ID in the link we emailed you (the part after <span className="font-mono">/report/</span> or <span className="font-mono">/health-checks/</span>).
                If you can&apos;t find it, <Link to="/contact" className="text-[#064E2E] underline">contact us</Link> and we&apos;ll resend.
              </p>
            </div>

            {error && (
              <div className="bg-[#B91C1C]/8 border border-[#B91C1C]/30 rounded-md px-3 py-2 text-sm text-[#B91C1C] flex items-start gap-2" data-testid="my-hc-error">
                <AlertTriangle size={14} className="mt-0.5 shrink-0"/> {error}
              </div>
            )}

            <button type="submit" disabled={loading} className="btn-primary w-full" data-testid="my-hc-submit">
              {loading ? <Loader2 size={14} className="animate-spin"/> : <ClipboardCheck size={14}/>}
              View my reports
            </button>
          </form>
        ) : (
          <div className="mt-8" data-testid="my-hc-results">
            {error && (
              <div className="bg-[#B91C1C]/8 border border-[#B91C1C]/30 rounded-md px-3 py-2 text-sm text-[#B91C1C] flex items-start gap-2 mb-4">
                <AlertTriangle size={14} className="mt-0.5 shrink-0"/> {error}
              </div>
            )}
            {items.length === 0 ? (
              <div className="card-default text-center py-12" data-testid="my-hc-empty">
                <ClipboardCheck size={28} className="mx-auto text-[#94A3B8]"/>
                <h2 className="font-display text-xl text-[#071A33] mt-3">No reports yet</h2>
                <p className="text-[#475569] mt-2 text-sm max-w-sm mx-auto">
                  Your first Annual Health Check hasn&apos;t been published yet. We&apos;ll email you as soon as it&apos;s ready.
                </p>
                <Link to="/contact" className="btn-outline mt-5 inline-flex" data-testid="my-hc-empty-contact">
                  Contact us <ArrowRight size={14}/>
                </Link>
              </div>
            ) : (
              <>
                <div className="grid lg:grid-cols-3 gap-3 mb-5">
                  <SummaryCard label="Reports on file" value={items.length} tone="muted"/>
                  <SummaryCard
                    label="Latest rating"
                    value={items[0]?.overall_rating || "Pending"}
                    tone={ratingTone(items[0]?.overall_rating)}
                  />
                  <SummaryCard
                    label="Next inspection due"
                    value={items[0]?.next_due_at ? formatDate(items[0].next_due_at) : "—"}
                    tone="muted"
                  />
                </div>

                <ul className="space-y-4">
                  {items.map((h) => <HealthCheckRow key={h.id} h={h}/>)}
                </ul>
              </>
            )}

            <button
              onClick={() => { setItems(null); setError(""); }}
              className="btn-outline mt-6 text-sm"
              data-testid="my-hc-look-again"
            >
              Look up another email
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function ratingTone(r) {
  if (r === "RED") return "red";
  if (r === "AMBER") return "amber";
  if (r === "GREEN") return "green";
  return "muted";
}

function SummaryCard({ label, value, tone }) {
  const cls = tone === "green" ? "border-[#064E2E]/30 bg-[#064E2E]/5 text-[#064E2E]"
            : tone === "amber" ? "border-[#B88A2A]/30 bg-[#B88A2A]/5 text-[#B88A2A]"
            : tone === "red"   ? "border-[#B91C1C]/30 bg-[#B91C1C]/5 text-[#B91C1C]"
            : "border-[#E2DCD0] bg-white text-[#071A33]";
  return (
    <div className={`border rounded-xl p-5 ${cls}`} data-testid={`my-hc-summary-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <p className="text-[10px] tracking-[0.22em] uppercase font-semibold opacity-80">{label}</p>
      <p className="font-display text-3xl tabular-nums mt-1 tracking-tight">{value}</p>
    </div>
  );
}

function HealthCheckRow({ h }) {
  const t = RATING_TILE[h.overall_rating] || { bg: "bg-[#475569]", label: "Pending", icon: <ClipboardCheck size={14}/> };
  const reportUrl = h.public_token ? `${BACKEND}/api/health-checks/${h.public_token}/pdf` : null;
  const viewerUrl = h.public_token ? `/report/${h.public_token}` : null; // currently used by quote reviews; HC has its own public page

  return (
    <li className="card-default" data-testid={`my-hc-item-${h.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex-1 min-w-[260px]">
          <p className="eyebrow">Annual Health Check</p>
          <h3 className="font-display text-2xl text-[#071A33] mt-1 leading-tight">{h.member_name || "Your home"}</h3>
          <p className="text-sm text-[#475569] mt-0.5">{h.park_name ? `${h.park_name} · ` : ""}{h.property_address || ""}</p>
          <div className="mt-3 grid sm:grid-cols-3 gap-3 text-xs">
            <Meta label="Completed" value={formatDate(h.completed_at)}/>
            <Meta label="Next due" value={formatDate(h.next_due_at)} icon={<CalendarClock size={11}/>}/>
            <Meta label="Recommendations" value={String((h.recommendations || []).length)}/>
          </div>
        </div>
        <div className={`px-3 py-2 rounded-lg text-white ${t.bg} flex items-center gap-2 shrink-0`} data-testid={`my-hc-rating-${h.id}`}>
          {t.icon}
          <div className="leading-tight">
            <p className="text-[10px] tracking-[0.18em] uppercase opacity-80">Overall</p>
            <p className="font-semibold">{h.overall_rating || "PENDING"} · {t.label}</p>
          </div>
        </div>
      </div>

      {(h.recommendations || []).length > 0 && (
        <details className="mt-4 group" data-testid={`my-hc-recs-${h.id}`}>
          <summary className="text-sm text-[#064E2E] cursor-pointer font-medium hover:underline list-none flex items-center gap-1">
            <span>View recommendations</span>
            <ArrowRight size={12} className="group-open:rotate-90 transition"/>
          </summary>
          <ul className="mt-3 space-y-2">
            {h.recommendations.map((r, i) => (
              <li key={i} className="border-l-2 border-[#B88A2A]/40 pl-3 py-1">
                <p className="text-[10px] tracking-[0.18em] uppercase font-semibold text-[#475569]">
                  <span className={
                    r.priority === "HIGH" ? "text-[#B91C1C]" :
                    r.priority === "LOW" ? "text-[#0F766E]" : "text-[#B88A2A]"
                  }>{r.priority || "MED"}</span>
                  <span className="text-[#94A3B8] mx-1.5">·</span>
                  <span>{(r.timescale || "").replace("_", " ").toLowerCase()}</span>
                </p>
                <p className="text-sm text-[#071A33] mt-0.5">{r.text}</p>
              </li>
            ))}
          </ul>
        </details>
      )}

      <div className="mt-4 flex flex-wrap gap-2">
        {reportUrl && (
          <a
            href={reportUrl}
            target="_blank"
            rel="noreferrer"
            className="btn-primary text-sm"
            data-testid={`my-hc-download-${h.id}`}
          >
            <Download size={14}/> Download report (PDF)
          </a>
        )}
        {viewerUrl && (
          <Link
            to={`/health-checks/${h.public_token}`}
            className="btn-outline text-sm"
            data-testid={`my-hc-view-${h.id}`}
          >
            View report online <ArrowRight size={14}/>
          </Link>
        )}
      </div>
    </li>
  );
}

function Meta({ label, value, icon }) {
  return (
    <div>
      <p className="text-[10px] tracking-[0.18em] uppercase text-[#475569] font-semibold flex items-center gap-1">{icon}{label}</p>
      <p className="text-[#071A33] mt-0.5">{value || "—"}</p>
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return String(iso).slice(0, 10); }
}
